import { en } from "./en/index.ts";
import { it } from "./it/index.ts";

/**
 * Traduzione dei testi, per il server e per l'interfaccia.
 *
 * Una voce di catalogo è una stringa o una funzione che riceve parametri
 * tipizzati: chi chiede una chiave con i parametri sbagliati, o una chiave che
 * non esiste, lo scopre dal typecheck. Nessuna libreria: plurali, elenchi e
 * date li sa già fare `Intl`.
 */

/** Aggiungere una lingua: un catalogo in `./<codice>/` e una riga qui. */
export const LINGUE = [
  { codice: "it", nome: "Italiano" },
  { codice: "en", nome: "English" },
] as const;

export type Lingua = (typeof LINGUE)[number]["codice"];
export type Catalogo = typeof it;
export type Chiave = keyof Catalogo;
export type Parametri<K extends Chiave> = Catalogo[K] extends (p: infer P) => string ? [parametri: P] : [];

/** Una frase non ancora scritta: viaggia così dal server, e si traduce a destinazione. */
export interface Messaggio {
  readonly chiave: string;
  readonly parametri?: Record<string, unknown>;
}

const CATALOGHI: Record<Lingua, Catalogo> = { it, en };

export function m<K extends Chiave>(chiave: K, ...p: Parametri<K>): Messaggio {
  return p[0] === undefined ? { chiave } : { chiave, parametri: p[0] as Record<string, unknown> };
}

export interface Traduttore {
  readonly lingua: Lingua;
  t<K extends Chiave>(chiave: K, ...p: Parametri<K>): string;
  /** Null se la chiave non c'è: chi riceve un messaggio da un server più nuovo sceglie il ripiego. */
  traduci(messaggio: Messaggio): string | null;
  data(valore: Date | string | number, stile?: "breve" | "completa"): string;
  /** «2 ore fa», «ieri»: l'unità più grande non nulla. */
  tempoFa(valore: Date | number, ora?: number): string;
  numero(valore: number, opzioni?: Intl.NumberFormatOptions): string;
  elenco(voci: readonly string[]): string;
}

export function creaTraduttore(lingua: Lingua): Traduttore {
  const catalogo = CATALOGHI[lingua] as Record<string, string | ((p: object) => string)>;
  const date = {
    breve: new Intl.DateTimeFormat(lingua, { dateStyle: "short", timeStyle: "short" }),
    completa: new Intl.DateTimeFormat(lingua, { dateStyle: "long", timeStyle: "short" }),
  };
  const relativo = new Intl.RelativeTimeFormat(lingua, { numeric: "auto" });
  const UNITA: [Intl.RelativeTimeFormatUnit, number][] = [
    ["year", 31_536_000],
    ["month", 2_592_000],
    ["day", 86_400],
    ["hour", 3_600],
    ["minute", 60],
    ["second", 1],
  ];
  const elenchi = new Intl.ListFormat(lingua, { type: "conjunction" });

  const traduci = (messaggio: Messaggio): string | null => {
    // Solo chiavi proprie: «constructor» o «toString» non sono voci del catalogo.
    if (!Object.hasOwn(catalogo, messaggio.chiave)) return null;
    const voce = catalogo[messaggio.chiave]!;
    if (typeof voce === "string") return voce;
    // Parametri di un'altra forma (server più nuovo dell'interfaccia): chi chiama
    // ripiega sul proprio testo invece di far cadere la pagina.
    try {
      return voce(messaggio.parametri ?? {});
    } catch {
      return null;
    }
  };

  return {
    lingua,
    t: (chiave, ...p) => traduci(m(chiave, ...p)) ?? chiave,
    traduci,
    data: (valore, stile = "breve") => date[stile].format(new Date(valore)),
    tempoFa: (valore, ora = Date.now()) => {
      const secondi = Math.round((new Date(valore).getTime() - ora) / 1000);
      const [unita, durata] = UNITA.find(([, d]) => Math.abs(secondi) >= d) ?? UNITA[5]!;
      return relativo.format(Math.trunc(secondi / durata), unita);
    },
    numero: (valore, opzioni) => new Intl.NumberFormat(lingua, opzioni).format(valore),
    elenco: (voci) => elenchi.format(voci),
  };
}

export function linguaSupportata(valore: string | null | undefined): Lingua | null {
  const codice = valore?.split("-")[0]?.toLowerCase();
  return LINGUE.find((l) => l.codice === codice)?.codice ?? null;
}
